/* =============================================================================
 * Avatar — APS primitive (Track D)
 * School crest / athlete monogram with an initials fallback. Optional success
 * ring + badge slot for the coach-open / engaged state (used on inbox rows and
 * reveal cards). Rounded-square by default (crest), or `circle`.
 * ========================================================================== */
import React from 'react';
import { radius, font } from '../../styles/tokens';

export interface AvatarProps {
  name?: string;
  src?: string;
  size?: number;
  bg?: string;
  circle?: boolean;
  ring?: boolean;
  badge?: React.ReactNode;
  style?: React.CSSProperties;
}

function initials(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export const Avatar: React.FC<AvatarProps> = ({
  name, src, size = 44, bg = 'var(--aps-ink)', circle, ring, badge, style,
}) => {
  const r = circle ? radius.pill : Math.round(size * 0.25);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, ...style }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: r,
          background: src ? `center/cover no-repeat url(${src})` : bg,
          color: 'var(--aps-on-ink)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: font.sans,
          fontWeight: font.weight.heavy,
          fontSize: Math.round(size * 0.34),
          boxShadow: ring ? '0 0 0 2px var(--aps-success-border-strong)' : undefined,
          boxSizing: 'border-box',
        }}
        aria-hidden={!name}
      >
        {!src && initials(name)}
      </div>
      {badge && (
        <div style={{ position: 'absolute', right: -4, bottom: -4 }}>{badge}</div>
      )}
    </div>
  );
};

export default Avatar;
